/**
 * Display order for User Insert / «Tilpass mal før innlasting».
 * Default: graph hierarchy (sources → targets), then canvas y/x.
 * Optional override: node.data.insert_order (lower = earlier).
 */

function canvasRank(node, originalIndex) {
  const y = Number(node?.y);
  const x = Number(node?.x);
  const yi = Number.isFinite(y) ? y : 0;
  const xi = Number.isFinite(x) ? x : 0;
  return { y: yi, x: xi, i: originalIndex };
}

/**
 * Kahn topological order; ties broken by canvas position then original index.
 * @returns {Map<string, number>}
 */
export function hierarchyIndexByNodeId(nodes, edges) {
  const list = Array.isArray(nodes) ? nodes : [];
  const ids = list.map((n) => n?.id).filter((id) => id != null && id !== "");
  const nodeMap = new Map(list.map((n) => [n.id, n]));
  const orig = new Map(ids.map((id, i) => [id, i]));
  const idSet = new Set(ids);
  const indegree = new Map(ids.map((id) => [id, 0]));
  const outs = new Map(ids.map((id) => [id, []]));

  (Array.isArray(edges) ? edges : []).forEach((edge) => {
    const source = edge?.source;
    const target = edge?.target;
    if (!idSet.has(source) || !idSet.has(target) || source === target) return;
    indegree.set(target, (indegree.get(target) || 0) + 1);
    outs.get(source).push(target);
  });

  const compareReady = (a, b) => {
    const ra = canvasRank(nodeMap.get(a), orig.get(a) ?? 0);
    const rb = canvasRank(nodeMap.get(b), orig.get(b) ?? 0);
    if (ra.y !== rb.y) return ra.y - rb.y;
    if (ra.x !== rb.x) return ra.x - rb.x;
    return ra.i - rb.i;
  };

  let ready = ids.filter((id) => (indegree.get(id) || 0) === 0).sort(compareReady);
  const order = [];
  const seen = new Set();

  while (ready.length) {
    const id = ready.shift();
    if (seen.has(id)) continue;
    seen.add(id);
    order.push(id);
    const nextReady = [];
    (outs.get(id) || []).forEach((target) => {
      const next = (indegree.get(target) || 0) - 1;
      indegree.set(target, next);
      if (next === 0 && !seen.has(target)) nextReady.push(target);
    });
    if (nextReady.length) {
      ready = [...ready, ...nextReady].sort(compareReady);
    }
  }

  // Cycles / unreachable: append remaining in canvas order
  ids
    .filter((id) => !seen.has(id))
    .sort(compareReady)
    .forEach((id) => order.push(id));

  return new Map(order.map((id, index) => [id, index]));
}

export function readInsertOrder(node) {
  const raw =
    node?.data?.insert_order ??
    node?.data?.insertOrder ??
    node?.data?.wizard_order;
  if (raw == null || raw === "") return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

/**
 * Stable display order for insert/wizard: custom insert_order, else hierarchy.
 * When a custom order equals an auto hierarchy index, the custom node wins (pins earlier).
 */
export function sortNodesForInsertDisplay(nodes, edges) {
  const list = Array.isArray(nodes) ? [...nodes] : [];
  if (list.length <= 1) return list;

  const hierarchyRank = hierarchyIndexByNodeId(list, edges);
  const orig = new Map(list.map((n, i) => [n.id, i]));

  return list.sort((a, b) => {
    const customA = readInsertOrder(a);
    const customB = readInsertOrder(b);
    const hA = hierarchyRank.get(a.id) ?? 0;
    const hB = hierarchyRank.get(b.id) ?? 0;
    const keyA = customA != null ? customA : hA;
    const keyB = customB != null ? customB : hB;
    if (keyA !== keyB) return keyA - keyB;
    if ((customA != null) !== (customB != null)) {
      return customA != null ? -1 : 1;
    }
    if (hA !== hB) return hA - hB;
    return (orig.get(a.id) ?? 0) - (orig.get(b.id) ?? 0);
  });
}
